/**
 * CR042: Admin → AI Providers & Budgets page
 * Configure AI provider settings and budget controls
 */

import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Save, Brain, DollarSign } from "lucide-react";
import { AdminLayout } from "@/components/admin/AdminLayout";

interface AIProviderSettings {
  id: string;
  provider: string;
  default_model: string;
  scan_model: string;
  validation_model: string | null;
  monthly_budget_usd: number;
  hard_stop_on_budget: boolean;
}

export default function AIProviders() {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState<AIProviderSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [provider, setProvider] = useState("google_gemini");
  const [defaultModel, setDefaultModel] = useState("gemini-2.5-flash");
  const [scanModel, setScanModel] = useState("gemini-2.5-flash-lite");
  const [validationModel, setValidationModel] = useState("gemini-2.5-flash");
  const [monthlyBudget, setMonthlyBudget] = useState("10.00");
  const [hardStop, setHardStop] = useState(false);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("ai_provider_settings")
        .select("*")
        .limit(1)
        .single();

      if (error && error.code !== "PGRST116") throw error;

      if (data) {
        setSettings(data);
        setProvider(data.provider);
        setDefaultModel(data.default_model);
        setScanModel(data.scan_model);
        setValidationModel(data.validation_model || "gemini-2.5-flash");
        setMonthlyBudget(data.monthly_budget_usd?.toString() || "10.00");
        setHardStop(data.hard_stop_on_budget);
      }
    } catch (err) {
      console.error("Error fetching AI provider settings:", err);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchSettings();
    }
  }, [isAdmin]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        provider,
        default_model: defaultModel,
        scan_model: scanModel,
        validation_model: validationModel || null,
        monthly_budget_usd: parseFloat(monthlyBudget) || 10.0,
        hard_stop_on_budget: hardStop,
      };

      if (settings) {
        const { error } = await supabase
          .from("ai_provider_settings")
          .update(data)
          .eq("id", settings.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from("ai_provider_settings").insert(data);

        if (error) throw error;
      }

      toast.success("Settings saved");
      fetchSettings();
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-muted-foreground">
          Admin access required
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">AI Providers & Budgets</h1>
          <p className="text-muted-foreground">
            Configure AI model settings and budget controls for POC scanning
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {/* Provider Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5" />
                  Model Configuration
                </CardTitle>
                <CardDescription>
                  Select which AI models to use for different tasks
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="provider">Provider</Label>
                  <Select value={provider} onValueChange={setProvider}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="google_gemini">Google Gemini</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="default_model">Default Model</Label>
                  <Select value={defaultModel} onValueChange={setDefaultModel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gemini-2.5-flash">Gemini 2.5 Flash</SelectItem>
                      <SelectItem value="gemini-2.5-flash-lite">Gemini 2.5 Flash-Lite</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Used for general AI tasks
                  </p>
                </div>

                <div>
                  <Label htmlFor="scan_model">Scan Model (POC)</Label>
                  <Select value={scanModel} onValueChange={setScanModel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gemini-2.5-flash-lite">
                        Gemini 2.5 Flash-Lite (Recommended)
                      </SelectItem>
                      <SelectItem value="gemini-2.5-flash">Gemini 2.5 Flash</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Used for initial page scanning in POC runs
                  </p>
                </div>

                <div>
                  <Label htmlFor="validation_model">Validation Model (Optional)</Label>
                  <Select value={validationModel} onValueChange={setValidationModel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gemini-2.5-flash">Gemini 2.5 Flash</SelectItem>
                      <SelectItem value="gemini-2.5-flash-lite">Gemini 2.5 Flash-Lite</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Used for optional validation step (if enabled)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Budget Controls */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Budget Controls
                </CardTitle>
                <CardDescription>
                  Set spending limits for AI API usage
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="monthly_budget">Monthly Budget (USD)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      $
                    </span>
                    <Input
                      id="monthly_budget"
                      type="number"
                      step="0.01"
                      min="0"
                      value={monthlyBudget}
                      onChange={(e) => setMonthlyBudget(e.target.value)}
                      className="pl-7"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Maximum monthly spend on AI API calls
                  </p>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label htmlFor="hard_stop">Hard Stop on Budget</Label>
                    <p className="text-xs text-muted-foreground">
                      Stop all AI calls when budget is reached
                    </p>
                  </div>
                  <Switch
                    id="hard_stop"
                    checked={hardStop}
                    onCheckedChange={setHardStop}
                  />
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm font-medium">POC Mode</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    For the POC, budget limits are advisory only. Hard stop is disabled
                    by default to allow full testing.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Settings
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
